import React from "react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { HeartHandshake, MessageSquare, ThumbsUp, LifeBuoy, Sparkles, Crown, type LucideIcon } from "lucide-react";
import {
  buildFollowUpGeneralMessage,
  buildFollowUpFeedbackMessage,
  buildFollowUpHelpMessage,
  buildFollowUpEncourageMessage,
  buildFollowUpBeneficiosGuruMessage,
  buildWhatsAppUrl,
  normalizeWhatsAppNumber,
  type FollowUpType,
} from "@/utils/whatsappTemplates";
import { trackEvent } from "@/utils/analyticsTrack";
import {
  recordAdminContact,
  fetchLastAdminContact,
  daysSinceDate,
  formatContactDateShort,
  contactTypeLabel,
  type AdminContactRecord,
} from "@/utils/adminContacts";

interface WhatsAppFollowUpButtonProps {
  user: {
    id: string;
    first_name?: string | null;
    email?: string | null;
    whatsapp?: string | null;
    phone?: string | null;
    plan?: string | null;
    subscription_end_date?: string | null;
    created_at?: string | null;
  };
}

const options: Array<{
  type: FollowUpType;
  label: string;
  icon: LucideIcon;
  builder: (u: WhatsAppFollowUpButtonProps["user"]) => string;
}> = [
  { type: "geral", label: "Acompanhamento geral", icon: MessageSquare, builder: buildFollowUpGeneralMessage },
  { type: "feedback", label: "Pedir feedback", icon: ThumbsUp, builder: buildFollowUpFeedbackMessage },
  { type: "ajuda", label: "Oferecer ajuda", icon: LifeBuoy, builder: buildFollowUpHelpMessage },
  { type: "incentivo", label: "Incentivar uso das ferramentas", icon: Sparkles, builder: buildFollowUpEncourageMessage },
  { type: "beneficios_guru", label: "Benefícios + Guru Chat (oferta)", icon: Crown, builder: buildFollowUpBeneficiosGuruMessage },
];

const WhatsAppFollowUpButton: React.FC<WhatsAppFollowUpButtonProps> = ({ user }) => {
  const phone = normalizeWhatsAppNumber(user.whatsapp || user.phone);
  const disabled = !phone;

  const [lastContact, setLastContact] = React.useState<AdminContactRecord | null>(null);

  React.useEffect(() => {
    let active = true;
    fetchLastAdminContact(user.id).then((c) => {
      if (active) setLastContact(c);
    });
    return () => {
      active = false;
    };
  }, [user.id]);

  const diasDesdeContato = daysSinceDate(lastContact?.created_at);
  const contatoRecente = diasDesdeContato !== null && diasDesdeContato < 7;

  const handleSelect = async (opt: (typeof options)[number]) => {
    if (!phone) return;
    const message = opt.builder(user);
    const url = buildWhatsAppUrl(phone, message);
    trackEvent("admin_whatsapp_acompanhamento_click", {
      origin: "admin_users",
      target_user_id: user.id,
      message_type: opt.type,
    });
    window.open(url, "_blank", "noopener,noreferrer");

    const saved = await recordAdminContact({
      targetUserId: user.id,
      contactType: opt.type,
      channel: "whatsapp",
      notes: opt.label,
    });
    if (saved) setLastContact(saved);
  };

  const trigger = (
    <Button
      size="sm"
      variant="ghost"
      disabled={disabled}
      className={
        contatoRecente
          ? "text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-900/20"
          : "text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
      }
    >
      <HeartHandshake size={16} />
    </Button>
  );

  if (disabled) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{trigger}</TooltipTrigger>
          <TooltipContent>Sem telefone cadastrado</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <DropdownMenu>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent>
              {lastContact
                ? `Último contato: ${contactTypeLabel(lastContact.contact_type)} em ${formatContactDateShort(lastContact.created_at)}`
                : "WhatsApp de Acompanhamento"}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel>Acompanhamento do Usuário</DropdownMenuLabel>
          {lastContact && (
            <div className="px-2 pb-2 text-[11px] text-muted-foreground">
              Último contato: {contactTypeLabel(lastContact.contact_type)} •{" "}
              {formatContactDateShort(lastContact.created_at)}
              {contatoRecente && (
                <span className="block text-amber-600 font-medium">
                  ⚠️ Enviado há {diasDesdeContato} dia(s) — evite repetir
                </span>
              )}
            </div>
          )}
          <DropdownMenuSeparator />
          {options.map((opt) => {
            const Icon = opt.icon;
            const jaEnviado = lastContact?.contact_type === opt.type && contatoRecente;
            return (
              <DropdownMenuItem key={opt.type} onClick={() => handleSelect(opt)} className="gap-2 cursor-pointer">
                <Icon size={14} className="text-indigo-600" />
                <span className="flex-1">{opt.label}</span>
                {jaEnviado && (
                  <span className="text-[10px] text-amber-600 whitespace-nowrap">
                    há {diasDesdeContato}d
                  </span>
                )}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
      {lastContact && (
        <span className="text-[10px] text-muted-foreground whitespace-nowrap">
          {formatContactDateShort(lastContact.created_at)}
        </span>
      )}
    </div>
  );
};

export default WhatsAppFollowUpButton;
